<template>
    <page :tabs="tabs">
        <grid ref="grid" :column-defs="columnDefs" :row-data="rowData" :is-auto-width="true" :statusBar="statusBar">
            <form-square-switch
                class="show-past-arrivals mb-1 mr-1"
                :labels="{
                    checked: 'Show Past Arrivals',
                    unchecked: 'Show Past Arrivals',
                }"
                v-model="showPastArrivals"
                @change="fetchData"
                :isToolbar="true"
                :sync="true"
            />
            <export-to-pdf-button :href="exportPdfHref" :pdfCacheKey="pdfCacheKey" />
        </grid>
    </page>
</template>

<script>
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
const exportToPdfButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
    )
);

export default {
    name: 'DigitalSignatureList',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        FormSquareSwitch,
        exportToPdfButton,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Pending Signatures',
            busy: false,
            rowData: [],
            statusBar: null,
            showPastArrivals: false,
            pdfCacheKey: null,
        };
    },
    mounted() {
        this.fetchData();
    },
    computed: {
        ...mapGetters('header', ['userCanUseUnlayer']),
        columnDefs() {
            return [
                {
                    field: 'invoice_id',
                    headerName: 'Invoice',
                    cellRenderer: ({ value }) => `<a href="/app/reservation/edit/invoice/${value}">${value}</a>`,
                },
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'arrival_date',
                    headerName: 'Arrival Date',
                    type: 'dateColumn',
                    minWidth: 100,
                    sort: this.showPastArrivals ? 'desc' : 'asc',
                },
                {
                    field: 'seen',
                    headerName: 'Seen',
                    type: 'yesNoColumn',
                },
                {
                    field: 'seen_date',
                    headerName: 'Seen On',
                    type: 'dateColumn',
                    minWidth: 100,
                    autoWidthExclude: true,
                    cellRendererParams: {
                        noValue: 'Not seen',
                    },
                },
            ];
        },
        exportPdfHref() {
            return `/app/digital-signature/signed-agreement/pending-pdf/`;
        },
        tabs() {
            const tabs = [
                {
                    title: 'Settings',
                    route: '/app/digital-signature/setup/',
                    active: false,
                },
                {
                    title: 'Requested Signatures',
                    route: '/app/digital-signature/list/',
                    active: false,
                },
                {
                    title: 'Pending Signatures',
                    route: '/app/digital-signature/pending/',
                    active: true,
                },
            ];
            if (this.userCanUseUnlayer) {
                tabs.push({
                    title: 'Assignments',
                    route: '/app/digital-signature/assignments/',
                    active: false,
                });
            }
            return tabs;
        },
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    `/app/digital-signature/signed-agreement/pending/?show_past_arrivals=${this.showPastArrivals}`
                );
                this.rowData = data.items;
                this.pdfCacheKey = data.pdf_cache_key;
            } catch (e) {
                this.errorToast({
                    text: 'Could not fetch pending signatures.',
                });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.show-past-arrivals {
    width: 157px;
    @include media-breakpoint-down(lg) {
        width: 179px;
    }
}
</style>
